<template>
  <v-dialog v-model="visible" width="auto" scrollable persistent>
    <bm-card
      :label="
        $m.error_has_occurred +
        (errorCode ? ` - ${errorCode}` : '') +
        ` - ${timestamp()}`
      "
    >
      <v-card-text data-qa="error-text"> {{ errorText }}</v-card-text>
      <template v-if="jsStack.length || serverStack.length || endpointName">
        <v-divider />
        <template v-if="expanded">
          <v-card-text v-if="endpointName"> {{ endpointName }}</v-card-text>
          <v-card-text class="stacktrace">
            <div v-for="(line, index) of jsStack" :key="index">
              {{ line }}
            </div>
          </v-card-text>
          <v-card-text class="stacktrace"
            ><div v-for="(line, index) of serverStack" :key="index">
              {{ line }}
            </div></v-card-text
          >
        </template>

        <bm-btn-expand v-model="expanded" block></bm-btn-expand>
      </template>

      <template #controls>
        <v-spacer />
        <bm-btn @click="clearErrorAndClose">{{ $m.close }}</bm-btn>
      </template>
    </bm-card>
  </v-dialog>
</template>

<script lang="ts">
import BmCard from "@/components/controls/card/BmCard.vue";
import appStore from "@/store/modules/appStore";
import { BrewManApiError, errorStatusCodeToTextLookup } from "@/app/errors";
import { ReactiveDialog } from "@/components/mixins/dialogs";
import userStore from "@/store/modules/userStore";
import { EnumUserTenantGroupLevel } from "@/app/services/generated/enums/enumUserTenantGroupLevel.generated";
import { defineComponent } from "vue";
import { isRunningEndToEndTests } from "@/app/ui/utilities";
export default defineComponent({
  name: "BmErrorDialog",
  components: { BmCard },
  mixins: [ReactiveDialog],
  data() {
    return {
      expanded: false as boolean,
    };
  },
  computed: {
    error(): Error | string | null {
      return appStore.state.error;
    },
    errorText(): string {
      if (typeof this.error === "string") {
        return this.error;
      }

      if (this.error instanceof Error) {
        return this.error.message;
      }

      return "";
    },
    jsStack(): string[] {
      if (this.error instanceof Error) {
        return (
          (this.error.stack && this.error.stack.split(/(\r\n|\n|\r)/)) || []
        );
      }

      return [];
    },
    serverStack(): string[] {
      if (this.error instanceof BrewManApiError) {
        return (
          (this.error.serverException &&
            this.error.serverException.split(/(\r\n|\n|\r)/)) ||
          []
        );
      }

      return [];
    },
    errorCode(): string {
      if (this.error instanceof BrewManApiError) {
        return errorStatusCodeToTextLookup[this.error.code] || "";
      }

      return "";
    },
    endpointName(): string {
      if (this.error instanceof BrewManApiError) {
        return this.error.endpoint || "";
      } else {
        return "";
      }
    },
  },
  watch: {
    error: {
      // @ts-ignore
      handler: "errorWatch",
      immediate: true,
    },
  },
  methods: {
    /**
     * Not a computed getter so to not cache the timestamp
     */
    timestamp(): string {
      const date = new Date();
      let timezoneFormatted = (-1 * date.getTimezoneOffset()) / 60;
      const tzString =
        (timezoneFormatted < 0 ? "-" : "+") + timezoneFormatted.toFixed(1);
      return `${date.toLocaleString()} (${tzString})`;
    },
    /**
     * React to errors by emitting an open event.
     */
    errorWatch(error: string) {
      if (error) {
        this.expanded =
          userStore.state.user?.userTenantGroupLevel ===
            EnumUserTenantGroupLevel.superAdministrator ||
          isRunningEndToEndTests();
        this.open();
      }
    },
    clearErrorAndClose() {
      // clear errors
      appStore.showError(null);
      this.accept();
    },
  },
});
</script>

<style scoped lang="sass">
/* Make stacktrace monospaced and indented */
.stacktrace
  font-family: "Courier New", Courier, monospace

.stacktrace div
  margin-left: 2em

.stacktrace div:first-child
  margin-left: 0
</style>
